import React, { useMemo } from 'react';
import Avatar from '@mui/material/Avatar';
import AttachMoney from '@mui/icons-material/AttachMoney';
import MenuBook from '@mui/icons-material/MenuBook';
import PropTypes from 'prop-types';
import dayjs from '../../../config/dayjs';
import SiiIcon from '../../icons/SiiIcon';

const colors = {
  finished: '#237A39',
  rejected: '#E40F0F',
  blocked: '#F8B147',
  default: '#D4D4D4',
};

const PAYMENT_API_CODES_DEFERRING_TO_SII = ['5', '6', '7', '9'];

const shouldDeferPaymentStatusToSii = (apiStatusCode) => (
  PAYMENT_API_CODES_DEFERRING_TO_SII.includes(String(apiStatusCode))
);

const validDictionary = (invoice) => {
  if (invoice.hasExecutiveMerit === undefined || invoice.hasExecutiveMerit === null) {
    const receptionMoment = dayjs(invoice.receptionDate);
    const daysToExecutiveMerit = 9 - dayjs().startOf('day').diff(receptionMoment.startOf('day'), 'days');
    const hasExecutiveMeritRecalculated = daysToExecutiveMerit <= 0;
    return { label: hasExecutiveMeritRecalculated
      ? 'Con mérito'
      : `${daysToExecutiveMerit} días para el mérito ejecutivo`,
    color: hasExecutiveMeritRecalculated ? colors.finished : colors.default,
    last: 'sii',
    };
  }
  return { label: invoice.hasExecutiveMerit
    ? 'Con mérito'
    : `${invoice.daysToExecutiveMerit} días para el mérito ejecutivo`,
  color: invoice.hasExecutiveMerit ? colors.finished : colors.default,
  last: 'sii',
  };
};

const siiStatusMaster = (invoice) => ({
  PROCESSING: { last: 'sii', color: colors.default, label: 'En proceso' },
  REJECTED_BY_SII: {
    last: 'sii',
    color: colors.rejected,
    label: 'Rechazado por SII',
  },
  REJECTED_RECEIVER: {
    last: 'sii',
    color: colors.rejected,
    label: 'Rechazado por Receptor',
  },
  REJECTED_CONTENT: {
    last: 'sii',
    color: colors.rejected,
    label: 'Con reclamos al contenido',
  },
  REJECTED_TOTAL: {
    last: 'sii',
    color: colors.rejected,
    label: 'Con reclamos por falta total',
  },
  REJECTED_FOR_TOTAL: {
    last: 'sii',
    color: colors.rejected,
    label: 'Rechazado por falta total',
  },
  REJECTED_PARTIAL: {
    last: 'sii',
    color: colors.rejected,
    label: 'Con reclamos por falta parcial',
  },
  VALID: validDictionary(invoice),
  RECEPTION_ACKNOWLEDGED: {
    last: 'sii',
    color: colors.finished,
    label: 'Con acuse de recibo',
  },
  PARTIAL_CLAIM: {
    last: 'sii',
    color: colors.rejected,
    label: 'Con rechazo por falta parcial de mercaderías',
  },
  TOTAL_CLAIM: {
    last: 'sii',
    color: colors.rejected,
    label: 'Con rechazo por falta total de mercaderías',
  },
  ALREADY_PAYED: {
    last: 'sii',
    color: colors.finished,
    label: 'Emitida al contado',
  },
  INVALID_STATUS: validDictionary(invoice),
  UNKNOWN_STATUS: validDictionary(invoice),
});

const SII_STATUS_REFERENCE_INVOICE = {
  hasExecutiveMerit: false,
  daysToExecutiveMerit: 0,
  receptionDate: dayjs(),
};

const REJECTED_SII_STATUSES = new Set(
  Object.entries(siiStatusMaster(SII_STATUS_REFERENCE_INVOICE))
    .filter(([, { color }]) => color === colors.rejected)
    .map(([status]) => status),
);

const isSiiServiceRejected = (siiStatus) => REJECTED_SII_STATUSES.has(siiStatus);

const resolveFinishedStepColor = (current, last, apiStatusCode, siiStatus, finishedSteps) => {
  if (!finishedSteps[last]?.includes(current)) return null;
  if (
    current === 'sii'
    && shouldDeferPaymentStatusToSii(apiStatusCode)
    && isSiiServiceRejected(siiStatus)
  ) {
    return colors.rejected;
  }
  return colors.finished;
};

const getSiiServiceDisplay = (invoice, siiStatus) => siiStatusMaster(invoice)[siiStatus];

const apiMaster = [
  { last: 'sii', color: colors.rejected },
  { last: 'accounting', color: colors.blocked },
  { last: 'payments', color: colors.blocked },
  { last: 'accounting', color: colors.finished },
  { last: 'payments', color: colors.finished },
  { last: 'payments', color: colors.finished },
  { last: 'payments', color: colors.finished },
  { last: 'sii', color: colors.default },
  { last: 'payments', color: colors.default },
];

const SiiCustomIconStepper = ({
  current,
  lastApiState,
  receptionDate,
  siiStatus,
  invoice,
}) => {
  const finishedSteps = {
    sii: [],
    accounting: ['sii'],
    payments: ['sii', 'accounting'],
  };

  const Icon = {
    sii: SiiIcon,
    accounting: MenuBook,
    payments: AttachMoney,
  }[current];
  const resolveColor = (last, color) => {
    if (last === current) return color;
    const finishedColor = resolveFinishedStepColor(
      current,
      last,
      lastApiState?.status?.code,
      siiStatus,
      finishedSteps,
    );
    if (finishedColor !== null) return finishedColor;
    return colors.default;
  };

  const iconColor = useMemo(() => {
    if (lastApiState?.rejectionCode) {
      return resolveColor('sii', colors.rejected);
    }
    if (lastApiState?.status) {
      const { last, color } = apiMaster[lastApiState.status.code - 1];
      return resolveColor(last, color);
    }
    const { last, color } = siiStatusMaster(invoice)[siiStatus];
    return resolveColor(last, color);
  }, [lastApiState, current, finishedSteps, receptionDate, siiStatus, invoice]);
  return (
    <Avatar sx={{ backgroundColor: iconColor, width: 28, height: 28 }}>
      <Icon fontSize="8px" sx={{ color: 'white' }} />
    </Avatar>
  );
};
const siiSteps = ['sii', 'accounting'];
export {
  SiiCustomIconStepper,
  siiSteps,
  siiStatusMaster,
  isSiiServiceRejected,
  shouldDeferPaymentStatusToSii,
  getSiiServiceDisplay,
};

SiiCustomIconStepper.propTypes = {
  siiStatus: PropTypes.string.isRequired,
  receptionDate: PropTypes.objectOf(dayjs).isRequired,
  current: PropTypes.string.isRequired,
  lastApiState: PropTypes.shape({
    rejectionCode: PropTypes.shape({
      reason: PropTypes.string,
    }),
    status: PropTypes.shape({
      code: PropTypes.string,
      status: PropTypes.string,
    }),
  }).isRequired,
  invoice: PropTypes.shape({
    id: PropTypes.string.isRequired,
    hasExecutiveMerit: PropTypes.bool.isRequired,
    daysToExecutiveMerit: PropTypes.number.isRequired,
  }).isRequired,
};
